import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { INSTRUMENT_LABELS, PITCH_RANGE, activePattern, notesFor } from '../../domain/factories';
import * as ops from '../../domain/projectOps';
import {
  DRUM_INSTRUMENTS,
  MELODIC_INSTRUMENTS,
  STEPS_PER_BAR,
  isDrum,
  type Instrument,
  type NoteEvent,
  type Pattern,
} from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { midiToNoteName, scaleNotesInRange } from '../../music/theory';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { Alert, Button, Panel, inputClass } from '../ui';
import { useOpRunner } from './useOpRunner';

type View = 'drums' | Instrument;
const COLORS: Record<Instrument, string> = {
  kick: 'bg-accent-500',
  snare: 'bg-pink-500',
  clap: 'bg-rose-400',
  hats: 'bg-cyan-400',
  perc: 'bg-teal-400',
  bass808: 'bg-amber-400',
  cowbell: 'bg-yellow-300',
  lead: 'bg-lime-400',
  pad: 'bg-violet-400',
};
const LENGTH_CHOICES = [0.5, 1, 2, 3, 4, 6, 8, 12, 16, 32];
const selectClass = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';

interface Focus {
  row: number;
  col: number;
}
/** The focused cell; drums select by step, melodic instruments by step and pitch. */
interface Selection {
  instrument: Instrument;
  step: number;
  pitch: number | null;
}

export function PatternEditor() {
  const { project } = useProject();
  const { audio } = useServices();
  const engine = useObservable(audio.state);
  const { apply, error } = useOpRunner();
  const pattern = activePattern(project);
  const [view, setView] = useState<View>('drums');
  const [page, setPage] = useState(0);
  const [selection, setSelection] = useState<Selection | null>(null);
  const pages = pattern.lengthSteps / STEPS_PER_BAR;
  const currentPage = Math.min(page, pages - 1);

  // Playhead within this pattern: only meaningful while looping it in pattern mode.
  const playStep = engine.status === 'playing' && project.transport.mode === 'pattern' ? engine.step % pattern.lengthSteps : -1;

  // Resolve the inspected note against the latest project data on every render.
  const inspected = selection
    ? notesFor(pattern, selection.instrument).find((n) => n.step === selection.step && (selection.pitch === null || n.pitch === selection.pitch))
    : undefined;

  return (
    <Panel title="Pattern editor">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="pattern-select" className="text-xs text-zinc-300">
            Pattern
          </label>
          <select
            id="pattern-select"
            className={selectClass}
            value={pattern.id}
            onChange={(e) => {
              setSelection(null);
              apply((p) => ops.setActivePattern(p, e.target.value));
            }}
          >
            {project.patterns.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.lengthSteps / STEPS_PER_BAR} bar{p.lengthSteps > 16 ? 's' : ''})
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="pattern-name" className="text-xs text-zinc-300">
            Name
          </label>
          <input
            id="pattern-name"
            className={`${inputClass} w-40`}
            value={pattern.name}
            maxLength={80}
            onChange={(e) => apply((p) => ops.renamePattern(p, pattern.id, e.target.value || 'Pattern'))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="pattern-length" className="text-xs text-zinc-300">
            Length
          </label>
          <select
            id="pattern-length"
            className={selectClass}
            value={pattern.lengthSteps}
            onChange={(e) => apply((p) => ops.setPatternLength(p, pattern.id, Number(e.target.value) as Pattern['lengthSteps']))}
          >
            <option value={16}>1 bar</option>
            <option value={32}>2 bars</option>
            <option value={64}>4 bars</option>
          </select>
        </div>
        <Button size="sm" onClick={() => apply((p) => ops.addPattern(p))}>
          New
        </Button>
        <Button size="sm" onClick={() => apply((p) => ops.duplicatePattern(p, pattern.id))}>
          Duplicate
        </Button>
        <Button size="sm" variant="ghost" className="text-red-300" onClick={() => apply((p) => ops.removePattern(p, pattern.id))}>
          Delete
        </Button>
      </div>
      {error && <Alert title={error} />}

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label="Instrument view" className="flex flex-wrap gap-1">
          {(['drums', ...MELODIC_INSTRUMENTS] as View[]).map((v) => (
            <button
              key={v}
              role="tab"
              type="button"
              aria-selected={view === v}
              onClick={() => {
                setView(v);
                setSelection(null);
              }}
              className={`rounded-md px-2.5 py-1 text-xs font-medium ${view === v ? 'bg-accent-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
              data-testid={`view-${v}`}
            >
              {v === 'drums' ? 'Drums' : INSTRUMENT_LABELS[v]}
            </button>
          ))}
        </div>
        {pages > 1 && (
          <div role="group" aria-label="Bar" className="flex gap-1">
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                type="button"
                aria-pressed={currentPage === i}
                onClick={() => setPage(i)}
                className={`rounded px-2 py-1 text-xs ${currentPage === i ? 'bg-zinc-200 text-zinc-900' : 'bg-zinc-800 text-zinc-300'}`}
              >
                Bar {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === 'drums' ? (
        <DrumGrid pattern={pattern} page={currentPage} playStep={playStep} onSelect={setSelection} apply={apply} />
      ) : (
        <PianoRoll pattern={pattern} instrument={view} page={currentPage} playStep={playStep} onSelect={setSelection} apply={apply} />
      )}

      <NoteInspector patternId={pattern.id} selection={selection} note={inspected} onSelect={setSelection} apply={apply} />
      <p className="mt-2 text-xs text-zinc-400">
        Click or press Enter/Space to add or remove a note; arrow keys move between cells. The focused note can be edited below.
      </p>
    </Panel>
  );
}

type Apply = ReturnType<typeof useOpRunner>['apply'];

function useRovingGrid(rows: number) {
  const [focus, setFocus] = useState<Focus>({ row: 0, col: 0 });
  const grid = useRef<HTMLDivElement>(null);
  const move = (row: number, col: number) => {
    setFocus({ row, col });
    grid.current?.querySelector<HTMLButtonElement>(`[data-cell="${row}-${col}"]`)?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, row: number, col: number) => {
    const moves: Record<string, [number, number]> = {
      ArrowRight: [row, (col + 1) % STEPS_PER_BAR],
      ArrowLeft: [row, (col - 1 + STEPS_PER_BAR) % STEPS_PER_BAR],
      ArrowDown: [(row + 1) % rows, col],
      ArrowUp: [(row - 1 + rows) % rows, col],
      Home: [row, 0],
      End: [row, STEPS_PER_BAR - 1],
    };
    const m = moves[event.key];
    if (m) {
      event.preventDefault();
      move(m[0], m[1]);
    }
  };
  return { focus, setFocus, grid, onKeyDown };
}

function DrumGrid({ pattern, page, playStep, onSelect, apply }: { pattern: Pattern; page: number; playStep: number; onSelect: (s: Selection) => void; apply: Apply }) {
  const { project } = useProject();
  const { focus, setFocus, grid, onKeyDown } = useRovingGrid(DRUM_INSTRUMENTS.length);
  const offset = page * STEPS_PER_BAR;
  return (
    <div className="overflow-x-auto pb-1">
      <div ref={grid} role="grid" aria-label={`Drum sequencer, bar ${page + 1}`} className="inline-flex min-w-full flex-col gap-1.5">
        {DRUM_INSTRUMENTS.map((instrument, row) => {
          const notes = notesFor(pattern, instrument);
          const muted = !ops.audibleInstruments(project.tracks, project.audioBus).has(instrument);
          return (
            <div role="row" key={instrument} className="flex items-center gap-2">
              <div role="rowheader" className="w-24 shrink-0 text-sm font-medium whitespace-nowrap">
                {INSTRUMENT_LABELS[instrument]}
              </div>
              <div className="flex gap-1">
                {Array.from({ length: STEPS_PER_BAR }, (_, col) => {
                  const step = offset + col;
                  const hits = notes.filter((n) => n.step === step);
                  const hit = hits[0];
                  const isFocus = focus.row === row && focus.col === col;
                  return (
                    <div role="gridcell" key={col}>
                      <button
                        type="button"
                        data-cell={`${row}-${col}`}
                        data-testid={`step-${instrument}-${step}`}
                        tabIndex={isFocus ? 0 : -1}
                        aria-pressed={hits.length > 0}
                        aria-label={`${INSTRUMENT_LABELS[instrument]} bar ${page + 1} step ${col + 1}${hit ? `, velocity ${Math.round(hit.velocity * 100)}%${hits.length > 1 ? `, ${hits.length} hits (roll)` : ''}` : ''}`}
                        onFocus={() => {
                          setFocus({ row, col });
                          onSelect({ instrument, step, pitch: null });
                        }}
                        onKeyDown={(e) => onKeyDown(e, row, col)}
                        onClick={() => {
                          apply((p) => ops.toggleDrumStep(p, pattern.id, instrument, step));
                          onSelect({ instrument, step, pitch: null });
                        }}
                        className={`relative size-7 rounded sm:size-8 ${hit ? COLORS[instrument] : col % 4 === 0 ? 'bg-zinc-700' : 'bg-zinc-800'} ${
                          playStep === step ? 'ring-2 ring-white/80' : ''
                        } ${muted ? 'opacity-40' : ''} hover:brightness-125`}
                        style={hit ? { opacity: muted ? 0.4 : 0.45 + hit.velocity * 0.55 } : undefined}
                      >
                        {hits.length > 1 && <span aria-hidden="true" className="absolute inset-x-0 bottom-0 text-[9px] font-bold text-zinc-900">{hits.length}</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PianoRoll({
  pattern,
  instrument,
  page,
  playStep,
  onSelect,
  apply,
}: {
  pattern: Pattern;
  instrument: Instrument;
  page: number;
  playStep: number;
  onSelect: (s: Selection) => void;
  apply: Apply;
}) {
  const { project } = useProject();
  const { root, scale } = project.settings.key;
  const notes = notesFor(pattern, instrument);
  const rows = useMemo(() => {
    const [low, high] = PITCH_RANGE[instrument];
    const set = new Set(scaleNotesInRange(root, scale, low, high));
    for (const n of notes) set.add(n.pitch);
    return [...set].sort((a, b) => b - a);
  }, [instrument, root, scale, notes]);
  const { focus, setFocus, grid, onKeyDown } = useRovingGrid(rows.length);
  const offset = page * STEPS_PER_BAR;
  return (
    <div className="max-h-96 overflow-auto pb-1">
      <div ref={grid} role="grid" aria-label={`${INSTRUMENT_LABELS[instrument]} piano roll, bar ${page + 1}`} className="inline-flex min-w-full flex-col gap-0.5">
        {rows.map((pitch, row) => {
          const name = midiToNoteName(pitch);
          const isRoot = (((pitch - root) % 12) + 12) % 12 === 0;
          return (
            <div role="row" key={pitch} className="flex items-center gap-2">
              <div role="rowheader" className={`w-12 shrink-0 text-right font-mono text-xs ${isRoot ? 'text-accent-300' : 'text-zinc-400'}`}>
                {name}
              </div>
              <div className="flex gap-0.5">
                {Array.from({ length: STEPS_PER_BAR }, (_, col) => {
                  const step = offset + col;
                  const start = notes.find((n) => n.step === step && n.pitch === pitch);
                  const sustain = !start && notes.some((n) => n.pitch === pitch && step > n.step && step < n.step + n.length);
                  const isFocus = focus.row === row && focus.col === col;
                  return (
                    <div role="gridcell" key={col}>
                      <button
                        type="button"
                        data-cell={`${row}-${col}`}
                        data-testid={`note-${instrument}-${step}-${pitch}`}
                        tabIndex={isFocus ? 0 : -1}
                        aria-pressed={Boolean(start)}
                        aria-label={`${name} bar ${page + 1} step ${col + 1}${start ? `, ${start.length} steps, velocity ${Math.round(start.velocity * 100)}%` : sustain ? ', held' : ''}`}
                        onFocus={() => {
                          setFocus({ row, col });
                          onSelect({ instrument, step, pitch });
                        }}
                        onKeyDown={(e) => onKeyDown(e, row, col)}
                        onClick={() => {
                          apply((p) => ops.toggleNote(p, pattern.id, instrument, step, pitch));
                          onSelect({ instrument, step, pitch });
                        }}
                        className={`h-5 w-6 rounded-sm sm:w-7 ${
                          start ? COLORS[instrument] : sustain ? `${COLORS[instrument]} opacity-40` : col % 4 === 0 ? 'bg-zinc-700/80' : 'bg-zinc-800'
                        } ${playStep === step ? 'ring-1 ring-white/80' : ''} hover:brightness-125`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NoteInspector({
  patternId,
  selection,
  note,
  onSelect,
  apply,
}: {
  patternId: string;
  selection: Selection | null;
  note: NoteEvent | undefined;
  onSelect: (s: Selection) => void;
  apply: Apply;
}) {
  if (!selection || !note) {
    return (
      <p className="mt-3 rounded-md border border-dashed border-zinc-800 p-2 text-xs text-zinc-400" data-testid="note-inspector">
        Focus a note to edit its velocity, length, timing and pitch.
      </p>
    );
  }
  const { instrument } = selection;
  const at = { step: note.step, pitch: note.pitch };
  const edit = (patch: Partial<NoteEvent>) => {
    if (apply((p) => ops.updateNote(p, patternId, instrument, at, patch)) && patch.pitch !== undefined && selection.pitch !== null) {
      onSelect({ ...selection, pitch: patch.pitch });
    }
  };
  return (
    <div className="mt-3 flex flex-wrap items-end gap-4 rounded-md border border-zinc-800 bg-zinc-950/60 p-3" role="group" aria-label="Note inspector" data-testid="note-inspector">
      <p className="text-sm font-medium">
        {INSTRUMENT_LABELS[instrument]} · step {note.step + 1}
        {!isDrum(instrument) && ` · ${midiToNoteName(note.pitch)}`}
      </p>
      <div className="flex flex-col gap-1">
        <label htmlFor="note-velocity" className="text-xs text-zinc-300">
          Velocity {Math.round(note.velocity * 100)}%
        </label>
        <input id="note-velocity" type="range" min={5} max={100} value={Math.round(note.velocity * 100)} onChange={(e) => edit({ velocity: Number(e.target.value) / 100 })} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="note-length" className="text-xs text-zinc-300">
          Length (steps)
        </label>
        <select id="note-length" className={selectClass} value={note.length} onChange={(e) => edit({ length: Number(e.target.value) })}>
          {[...new Set([...LENGTH_CHOICES, note.length])].sort((a, b) => a - b).map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="note-offset" className="text-xs text-zinc-300">
          Timing {note.offset > 0 ? '+' : ''}
          {Math.round(note.offset * 100)}% of a step
        </label>
        <input id="note-offset" type="range" min={-50} max={50} value={Math.round(note.offset * 100)} onChange={(e) => edit({ offset: Number(e.target.value) / 100 })} />
      </div>
      {(instrument === 'kick' || instrument === 'perc' || !isDrum(instrument)) && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-zinc-300" id="note-pitch-label">
            {isDrum(instrument) ? 'Tuning' : 'Pitch'} {midiToNoteName(note.pitch)}
          </span>
          <div className="flex gap-1" role="group" aria-labelledby="note-pitch-label">
            <Button size="sm" aria-label="Pitch down a semitone" onClick={() => edit({ pitch: note.pitch - 1 })}>
              −
            </Button>
            <Button size="sm" aria-label="Pitch up a semitone" onClick={() => edit({ pitch: note.pitch + 1 })}>
              +
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
